// Finance > Commissions — the bi-weekly payout worklist (2026-06-12; handoff
// W27 layout 2026-10-08).
//
// Reads GET /commissions/payable (earned-but-unpaid, grouped by rep) and
// marks a rep's batch paid via POST /commissions/pay { commission_ids,
// payroll_period }. This is the payout the commission engine drives toward:
// a rep earns once the client has paid AND every vendor bill is in; finance
// then pays it here and keys the same numbers into QB Payroll.
//
// Every amount is the engine's own figure — this page never derives a
// commission. Each row traces to its invoice, its quote and the
// project's Commissions tab (base, rate and split per row). The estimated /
// paid totals in the strip come from the Finance dashboard rollup.
//
// admin / finance only (matching the route).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { isFinanceRole, usePlanStatus } from "@/lib/compPlans";
import { CommissionsPlansPage } from "@/pages/commissions/CommissionsPlansPage";
import { BadgeDollarSign, Check, Settings, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { MetricStrip, TABLE_HEAD, TD, TH, TROW } from "@/components/ui/RecordParts";
import { cn } from "@/lib/utils";

interface CommissionLine {
  id: string;
  invoiceId: string;
  invoiceNumber: number;
  orderId: string;
  orderNumber: number;
  projectId: string;
  projectName: string;
  clientName: string;
  repRole: string;
  earnedAmount: string;
  earnedAt: string | null;
  // Order-level margins (CEO-corrected model 2026-07-21). These rows are all
  // EARNED, so billed margin is finalised from the actual bills.
  revenueCents: number;
  // hasFinalInvoice: dropped — the API sends it but this payout view (all rows
  // already earned) never reads it. Kept off the interface rather than carried
  // as dead surface.
  hasBills: boolean;
  bookedMarginCents: number;
  bookedMarginPctHundredths: number | null;
  billedMarginCents: number;
  billedMarginPctHundredths: number | null;
}

interface PayableRep {
  repId: string;
  repName: string | null;
  count: number;
  total: number;
  commissions: CommissionLine[];
}

interface PayableResponse {
  reps: PayableRep[];
  totalDue: number;
  count: number;
}

interface BucketCell {
  count: number;
  amount: string;
}
interface DashboardResponse {
  commissionsSummary: {
    estimated: BucketCell;
    earned: BucketCell;
    paid: BucketCell;
  };
  fetchedAt: string;
}

function money(n: number | string): string {
  const v = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

/** Integer cents → "$1,234.56". */
function fmtCents(cents: number): string {
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

/** Hundredths-of-a-percent → "42.35%"; null → em dash. */
function fmtPctH(h: number | null): string {
  return h == null ? "—" : `${(h / 100).toFixed(2)}%`;
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function roleLabel(r: string): string {
  if (r === "client_rep") return "Client rep";
  if (r === "order_rep") return "Order rep";
  if (r === "both") return "Client + order rep";
  return r.replace(/_/g, " ");
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Finance → Commissions entry (CEO 2026-10-15). Until the compensation-plan
 * database step runs (plan-status ready=false) this is today's payout page,
 * unchanged. Once ready: Finance / admin get the plan-based Commissions UI and
 * everyone else goes to their own statement.
 */
export function CommissionsPayoutPage() {
  const { user } = useAuth();
  const planStatus = usePlanStatus();
  const isFinance = isFinanceRole(user?.roles);
  if (planStatus.isLoading) {
    return <p className="p-6 text-[15px] text-muted">Loading…</p>;
  }
  if (planStatus.data?.ready) {
    return isFinance ? <CommissionsPlansPage /> : <Navigate to="/finance/commissions/statement" replace />;
  }
  return <LegacyCommissionsPayoutPage />;
}

function LegacyCommissionsPayoutPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage =
    user?.roles.some((r) => r === "admin" || r === "finance") ?? false;

  const [payrollPeriod, setPayrollPeriod] = useState(todayIso());
  const [confirmRep, setConfirmRep] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; msg: string } | null>(
    null,
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["commissions", "payable"],
    queryFn: () => api<PayableResponse>("/commissions/payable"),
    enabled: canManage,
  });

  const dashQ = useQuery({
    queryKey: ["finance", "dashboard"],
    queryFn: () => api<DashboardResponse>("/finance/dashboard"),
    enabled: canManage,
  });
  const cs = dashQ.data?.commissionsSummary;

  const pay = useMutation({
    mutationFn: (vars: { ids: string[] }) =>
      api<{ paid_count: number }>("/commissions/pay", {
        method: "POST",
        body: JSON.stringify({
          commission_ids: vars.ids,
          payroll_period: payrollPeriod.trim(),
        }),
      }),
    onSuccess: (res) => {
      setConfirmRep(null);
      setBanner({
        kind: "ok",
        msg:
          typeof res?.paid_count === "number"
            ? `Marked ${res.paid_count} ${res.paid_count === 1 ? "commission" : "commissions"} paid for payroll period ${payrollPeriod.trim()}. Key the same amounts into payroll.`
            : `Marked paid for payroll period ${payrollPeriod.trim()}. Key the same amounts into payroll.`,
      });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "payable"] });
      void queryClient.invalidateQueries({ queryKey: ["finance", "dashboard"] });
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  if (!canManage) {
    return (
      <div className="mx-auto w-full">
        <PageHeader eyebrow="Finance / Commissions" title="Commissions" />
        <p className="rounded-md border border-rule bg-white p-5 text-sm text-muted">
          Commissions payout is available to finance and admin users.
        </p>
      </div>
    );
  }

  const reps = data?.reps ?? [];
  const periodEmpty = payrollPeriod.trim().length === 0;

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Finance / Commissions"
        title="Commissions"
        intro="Commission a rep has earned — the client paid in full and every vendor bill is in — and that hasn't been paid out yet. Mark a rep's batch paid when you run payroll."
        action={
          <Link
            to="/finance/commissions/settings"
            className="inline-flex h-10 items-center gap-2 rounded-md border border-rule bg-white px-[14px] text-sm font-medium text-ink hover:bg-canvas"
          >
            <Settings className="h-4 w-4" /> Rates &amp; assignments
          </Link>
        }
      />

      <div className="mb-7">
        <MetricStrip
          ariaLabel="Commission summary"
          cells={[
            {
              label: "Estimated",
              value: dashQ.isError ? "—" : cs ? money(cs.estimated.amount) : null,
              note: cs ? `${cs.estimated.count.toLocaleString()} · not earned yet` : undefined,
            },
            {
              label: "Earned · ready to pay",
              value: isError ? "—" : data ? money(data.totalDue) : null,
              note: data ? `${data.count.toLocaleString()} ${data.count === 1 ? "commission" : "commissions"} · ${reps.length} ${reps.length === 1 ? "rep" : "reps"}` : undefined,
              attention: (data?.count ?? 0) > 0,
            },
            {
              label: "Paid",
              value: dashQ.isError ? "—" : cs ? money(cs.paid.amount) : null,
              note: cs ? `${cs.paid.count.toLocaleString()} paid out` : undefined,
            },
          ]}
        />
        <p className="mt-2 text-xs text-muted">
          Estimated → Earned → Paid. There&apos;s no separate approval step: marking a batch paid is the sign-off.
          Amounts are the commission engine&apos;s figures; each row links to its order.
        </p>
      </div>

      <section className="mb-6 flex flex-wrap items-end justify-between gap-4 rounded-md border border-rule bg-white p-[22px]">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Payroll period</h2>
          <p className="mt-1 text-sm text-muted">Saved on every commission you mark paid, so payroll can be traced back.</p>
        </div>
        <label className="text-xs text-muted">
          Period
          <input
            type="text"
            value={payrollPeriod}
            onChange={(e) => setPayrollPeriod(e.target.value)}
            placeholder="2026-06-15"
            className="mt-1 block h-10 w-44 rounded border border-rule bg-white px-3 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]"
          />
        </label>
      </section>

      {/* Banner */}
      {banner && (
        <div
          role={banner.kind === "err" ? "alert" : "status"}
          className={cn(
            "mb-6 rounded-md border px-4 py-3 text-sm",
            banner.kind === "ok"
              ? "border-ok/30 bg-ok-bg text-ok"
              : "border-risk/30 bg-risk-bg text-risk",
          )}
        >
          {banner.kind === "err" && <span className="font-medium">Nothing was marked paid. </span>}
          {banner.msg}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3 rounded-md border border-rule bg-white p-5" aria-label="Loading commissions">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
          ))}
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-5 text-sm text-risk">
          We couldn&apos;t load commissions. Nothing has changed — refresh to try again.
        </div>
      ) : reps.length === 0 ? (
        <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
          <BadgeDollarSign className="mx-auto h-6 w-6 text-muted" aria-hidden />
          <p className="mt-2 font-medium text-ink">Nothing to pay out right now.</p>
          <p className="mt-1 text-sm text-muted">
            Commissions show up here once an invoice is paid in full and all its vendor bills are in.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {reps.map((rep) => {
            const confirming = confirmRep === rep.repId;
            return (
              <section
                key={rep.repId}
                aria-label={`Commissions for ${rep.repName ?? "unknown rep"}`}
                className="overflow-hidden rounded-md border border-rule bg-white"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 px-[22px] py-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-canvas text-sm font-semibold text-ink"
                      aria-hidden
                    >
                      {(rep.repName ?? "?").charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
                        {rep.repName ?? "Unknown rep"}
                      </h2>
                      <p className="text-xs text-muted">
                        {rep.count} {rep.count === 1 ? "commission" : "commissions"} earned ·{" "}
                        <span className="font-medium tabular-nums text-ink">{money(rep.total)}</span>
                      </p>
                    </div>
                  </div>
                  {confirming ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-ink">
                        Pay {money(rep.total)} for {payrollPeriod.trim() || "—"}?
                      </span>
                      <Button
                        type="button"
                        disabled={pay.isPending || periodEmpty}
                        onClick={() =>
                          pay.mutate({ ids: rep.commissions.map((c) => c.id) })
                        }
                      >
                        <Check className="h-4 w-4" />
                        {pay.isPending ? "Paying…" : "Confirm"}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setConfirmRep(null)}>
                        <X className="h-4 w-4" /> Cancel
                      </Button>
                      {periodEmpty && (
                        <span className="w-full text-xs text-warn">Enter a payroll period first.</span>
                      )}
                    </div>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => {
                        setBanner(null);
                        setConfirmRep(rep.repId);
                      }}
                    >
                      <BadgeDollarSign className="h-4 w-4" /> Mark paid
                    </Button>
                  )}
                </div>

                <div className="overflow-x-auto border-t border-rule" role="region" aria-label={`${rep.repName ?? "Rep"} commission lines`} tabIndex={0}>
                  <table className="w-full text-left">
                    <thead className={TABLE_HEAD}>
                      <tr>
                        <th scope="col" className={TH}>Order</th>
                        <th scope="col" className={`${TH} hidden md:table-cell`}>Role</th>
                        <th scope="col" className={`${TH} hidden text-right lg:table-cell`}>Booked margin</th>
                        <th scope="col" className={`${TH} hidden text-right sm:table-cell`}>Billed margin</th>
                        <th scope="col" className={`${TH} hidden lg:table-cell`}>Earned</th>
                        <th scope="col" className={`${TH} text-right`}>Amount</th>
                      </tr>
                    </thead>
                    <tbody className="text-[13px]">
                      {rep.commissions.map((c) => (
                        <tr key={c.id} className={TROW}>
                          <td className={TD}>
                            <Link
                              to={`/projects/${c.projectId}/quotes/${c.orderId}`}
                              className="font-medium text-ink hover:underline"
                            >
                              Order #{c.orderNumber}
                            </Link>
                            <span className="block text-[11px] text-muted">
                              <Link to={`/projects/${c.projectId}/invoice/${c.invoiceId}`} className="hover:text-ink hover:underline">
                                Invoice #{c.invoiceNumber}
                              </Link>{" "}
                              · {c.clientName} · {c.projectName}
                            </span>
                            <Link
                              to={`/projects/${c.projectId}/commissions`}
                              className="mt-0.5 inline-block text-[11px] text-muted underline underline-offset-2 hover:text-ink"
                            >
                              Base, rate and split
                            </Link>
                          </td>
                          <td className={`${TD} hidden text-ink md:table-cell`}>
                            {roleLabel(c.repRole)}
                          </td>
                          <td
                            className={`${TD} hidden text-right tabular-nums text-muted lg:table-cell`}
                            title="Expected margin — the order as sold (rep-entered costs)"
                          >
                            {fmtCents(c.bookedMarginCents)}
                            <span className="block text-[11px]">{fmtPctH(c.bookedMarginPctHundredths)}</span>
                          </td>
                          <td className={`${TD} hidden text-right tabular-nums text-ink sm:table-cell`}>
                            {/* These rows are all EARNED, so billed margin is the
                                finalised number whether or not bills are attached
                                (a bill-less earned row is a genuine pure-service
                                order — show its real margin, never a fake
                                "awaiting"). */}
                            <span title="Actual margin — from the bills (product + freight + service)">
                              {fmtCents(c.billedMarginCents)}
                              <span className="block text-[11px] text-muted">
                                {fmtPctH(c.billedMarginPctHundredths)}
                              </span>
                            </span>
                          </td>
                          <td className={`${TD} hidden text-muted lg:table-cell`}>
                            {formatDate(c.earnedAt)}
                          </td>
                          <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                            {money(c.earnedAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
